/**
 * WeeklyDigestCard — last seven days of income against the seven before,
 * the stream that carried it, and anything that looks off.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, AlertTriangle, Info, Loader2 } from 'lucide-react';

const fmt = (v, cur) => `${cur} ${Number(v || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const WeeklyDigestCard = ({ currency }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['revenue-weekly-digest', currency],
    queryFn: () => api.get('/finance/revenue/weekly-digest', {
      params: currency ? { currency } : {},
    }).then((r) => r.data),
  });

  if (isError) return null;
  if (isLoading) {
    return (
      <Card data-testid="weekly-digest-card">
        <CardContent className="py-6 text-center">
          <Loader2 className="h-4 w-4 animate-spin text-slate-400 mx-auto" />
        </CardContent>
      </Card>
    );
  }

  const cur = data.display_currency;
  const hasChange = data.change_pct !== null && data.change_pct !== undefined;
  const up = (data.change_pct || 0) >= 0;
  const Trend = up ? TrendingUp : TrendingDown;

  return (
    <Card className="border-slate-200" data-testid="weekly-digest-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-500">
              Weekly digest · last 7 days
            </p>
            <p className="text-2xl font-bold text-slate-900 mt-0.5" data-testid="weekly-digest-income">
              {fmt(data.income, cur)}
            </p>
            <p className={`text-xs mt-0.5 flex items-center gap-1 ${up ? 'text-emerald-700' : 'text-rose-700'}`}
               data-testid="weekly-digest-change">
              <Trend className="h-3.5 w-3.5" />
              {hasChange
                ? `${up ? '+' : ''}${data.change_pct}% vs ${fmt(data.previous_income, cur)} the week before`
                : 'Nothing came in the week before, so there is no comparison yet'}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[11px] uppercase tracking-wide text-slate-500">Top stream</p>
            {data.top_stream ? (
              <>
                <p className="text-sm font-semibold text-slate-800" data-testid="weekly-digest-top-stream">
                  {data.top_stream.label}
                </p>
                <p className="text-xs text-slate-500">
                  {fmt(data.top_stream.total, cur)} · {data.top_stream.share_pct}% of the week
                </p>
              </>
            ) : (
              <p className="text-sm text-slate-400" data-testid="weekly-digest-top-stream">No income yet</p>
            )}
          </div>
          <div className="text-right">
            <p className="text-[11px] uppercase tracking-wide text-slate-500">Net</p>
            <p className="text-sm font-semibold text-slate-800" data-testid="weekly-digest-net">
              {fmt(data.net, cur)}
            </p>
            <p className="text-xs text-slate-500">
              {data.transaction_count} transaction{data.transaction_count === 1 ? '' : 's'}
            </p>
          </div>
        </div>

        {(data.flags || []).length > 0 && (
          <div className="space-y-1.5 pt-1" data-testid="weekly-digest-flags">
            {data.flags.map((flag, i) => {
              const warn = flag.level === 'warning';
              const Icon = warn ? AlertTriangle : Info;
              return (
                <p key={i} data-testid={`weekly-digest-flag-${i}`}
                   className={`text-xs flex items-start gap-1.5 ${warn ? 'text-amber-800' : 'text-slate-600'}`}>
                  <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  {flag.message}
                </p>
              );
            })}
          </div>
        )}

        {(data.streams || []).length > 1 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {data.streams.slice(0, 6).map((s) => (
              <Badge key={s.key} variant="outline" className="text-[10px]"
                     data-testid={`weekly-digest-stream-${s.key}`}>
                {s.label} {fmt(s.total, cur)}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WeeklyDigestCard;
